import { useNavigate } from "@tanstack/react-router";
import { useState } from "react";

import { BrandHeader } from "../components/BrandHeader";
import { saveTokens } from "../lib/auth-storage";
import { DOLMIT } from "../tokens";

function readFromParam(): string | null {
  if (typeof window === "undefined") return null;
  const raw = new URLSearchParams(window.location.search).get("from");
  // Same-origin path only — never blindly redirect to an absolute URL.
  if (raw?.startsWith("/") && !raw.startsWith("//")) return raw;
  return null;
}

const C = DOLMIT;

export function LoginRoute() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [emailFocus, setEmailFocus] = useState(false);
  const [passwordFocus, setPasswordFocus] = useState(false);
  const [hover, setHover] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const res = await fetch("/api/auth/login", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      if (res.status === 200) {
        const body = (await res.json()) as { accessToken: string; refreshToken: string };
        saveTokens({ access: body.accessToken, refresh: body.refreshToken });
        const from = readFromParam();
        // `href:` is the untyped escape hatch — `from` is already same-origin
        // path-validated by readFromParam(). Casting `to:` to a literal
        // bypasses the route-tree check; `href:` simply navigates.
        await navigate({ href: from ?? "/" });
        return;
      }
      if (res.status === 401) {
        setError("Invalid email or password.");
        return;
      }
      setError("Login failed. Please try again or contact your administrator.");
    } catch (_err) {
      setError("Network error. Please try again.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <main
      style={{
        minHeight: "100vh",
        background: C.canvas,
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        padding: "100px 24px 24px",
        fontFamily: "'Source Sans Pro',system-ui,sans-serif",
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 380,
          display: "flex",
          flexDirection: "column",
          alignItems: "stretch",
        }}
      >
        <div
          style={{
            background: C.surface,
            borderRadius: 12,
            border: `1px solid ${C.line}`,
            boxShadow: C.shM,
            padding: "32px 32px 28px",
          }}
        >
          <div style={{ marginBottom: 28 }}>
            <BrandHeader size="lg" />
          </div>
          <h1
            style={{
              fontSize: 18,
              fontWeight: 600,
              color: C.navyDeep,
              margin: "0 0 4px",
              fontFamily: "'Poppins',system-ui,sans-serif",
              textAlign: "center",
            }}
          >
            Sign in
          </h1>
          <p
            style={{
              fontSize: 12.5,
              color: C.slateMut,
              margin: "0 0 22px",
              textAlign: "center",
            }}
          >
            Use your Resource Planner account to continue.
          </p>
          <form onSubmit={onSubmit} noValidate>
            <Field label="Email">
              <input
                type="email"
                autoComplete="username"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onFocus={() => setEmailFocus(true)}
                onBlur={() => setEmailFocus(false)}
                style={inputStyle(emailFocus)}
              />
            </Field>
            <Field label="Password">
              <input
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onFocus={() => setPasswordFocus(true)}
                onBlur={() => setPasswordFocus(false)}
                style={inputStyle(passwordFocus)}
              />
            </Field>
            {error && (
              <div
                role="alert"
                style={{
                  fontSize: 12.5,
                  color: C.r,
                  background: C.rBg,
                  border: `1px solid ${C.rBd}`,
                  borderRadius: 6,
                  padding: "8px 12px",
                  marginBottom: 14,
                }}
              >
                {error}
              </div>
            )}
            <button
              type="submit"
              disabled={busy}
              onMouseEnter={() => setHover(true)}
              onMouseLeave={() => setHover(false)}
              style={{
                width: "100%",
                padding: "10px 14px",
                borderRadius: 6,
                border: `1px solid ${hover && !busy ? C.blueHover : C.blue}`,
                background: busy ? C.blueTint5 : hover ? C.blueHover : C.blue,
                color: "#fff",
                fontSize: 13,
                fontWeight: 600,
                cursor: busy ? "not-allowed" : "pointer",
                opacity: busy ? 0.7 : 1,
                transition: "all .12s",
                fontFamily: "'Source Sans Pro',system-ui,sans-serif",
              }}
            >
              {busy ? "Signing in…" : "Sign in"}
            </button>
          </form>
        </div>
        <div
          style={{
            fontSize: 11,
            color: C.slateMut,
            textAlign: "center",
            marginTop: 10,
            opacity: 0.7,
            fontFamily: "'Source Sans Pro',system-ui,sans-serif",
          }}
        >
          v{import.meta.env["VITE_APP_VERSION"]}
        </div>
      </div>
    </main>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: "block", marginBottom: 14 }}>
      <span
        style={{
          display: "block",
          fontSize: 11,
          fontWeight: 600,
          color: C.slateMut,
          textTransform: "uppercase",
          letterSpacing: 0.4,
          marginBottom: 5,
        }}
      >
        {label}
      </span>
      {children}
    </label>
  );
}

function inputStyle(focused: boolean): React.CSSProperties {
  return {
    width: "100%",
    padding: "8px 11px",
    borderRadius: 6,
    border: `1px solid ${focused ? C.blue : C.line}`,
    outline: "none",
    fontSize: 13,
    color: C.navy,
    background: C.surface,
    boxSizing: "border-box",
    fontFamily: "'Source Sans Pro',system-ui,sans-serif",
    transition: "border-color .12s",
  };
}
